import { useState, useMemo } from "react"
import assetQRDeWP from '@/assets/QR_deWP.svg.png'
import {
  Plus, Edit2, Trash2, Copy, Check, QrCode,
  ArrowLeft, Search, Monitor, Key, Lock, Tv2,
} from "lucide-react"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Label } from "../ui/label"

interface Room {
  id: string
  roomName: string
  roomDescription: string
  activationKey: string
  password: string
  status: "online" | "offline" | "idle"
  device: string
  lastActive: string
}

const MOCK_ROOMS: Room[] = [
  { id: "r1", roomName: "Boardroom A",     roomDescription: "Main executive boardroom on floor 4",    activationKey: "AK-2024-BRDA-001", password: "Br@2024!",    status: "online",  device: "InMeet Room Pro",  lastActive: "Active now" },
  { id: "r2", roomName: "Conference Hall B", roomDescription: "Large conference hall for all-hands",   activationKey: "AK-2024-CHB-002",  password: "Ch@2024!",    status: "online",  device: "InMeet Room Plus", lastActive: "Active now" },
  { id: "r3", roomName: "Huddle Room 1",   roomDescription: "Small huddle space for quick standups",  activationKey: "AK-2024-HR1-003",  password: "Hu@2024!",    status: "idle",    device: "InMeet Room Mini", lastActive: "2 hrs ago" },
  { id: "r4", roomName: "Huddle Room 2",   roomDescription: "Small huddle space near marketing",      activationKey: "AK-2024-HR2-004",  password: "Hu@2024#2",   status: "offline", device: "InMeet Room Mini", lastActive: "Yesterday" },
  { id: "r5", roomName: "Training Room",   roomDescription: "Training and workshop space, floor 2",   activationKey: "AK-2024-TRN-005",  password: "Tr@2024!",    status: "online",  device: "InMeet Room Pro",  lastActive: "Active now" },
  { id: "r6", roomName: "War Room",        roomDescription: "Dedicated ops and incident response room",activationKey: "AK-2024-WAR-006",  password: "Wa@2024!",    status: "offline", device: "InMeet Room Plus", lastActive: "3 days ago" },
]

const STATUS_STYLES = {
  online:  "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  offline: "bg-slate-500/10 text-muted-foreground border-border",
  idle:    "bg-amber-500/10 text-amber-600 border-amber-500/20",
}
const STATUS_DOT = {
  online:  "bg-emerald-500",
  offline: "bg-muted-foreground/40",
  idle:    "bg-amber-500",
}

interface AddEditDialogProps {
  room: Partial<Room> | null
  onSave: (r: Partial<Room>) => void
  onClose: () => void
}
function AddEditDialog({ room, onSave, onClose }: AddEditDialogProps) {
  const [form, setForm] = useState({
    roomName:        room?.roomName        ?? "",
    roomDescription: room?.roomDescription ?? "",
    activationKey:   room?.activationKey   ?? `AK-${Date.now()}`,
    password:        room?.password        ?? "",
    device:          room?.device          ?? "InMeet Room Pro",
  })
  const isEdit = !!room?.id
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Room" : "Add New Room"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="roomName">Room Name *</Label>
            <Input
              id="roomName"
              autoFocus
              className="mt-1"
              value={form.roomName}
              onChange={e => setForm(f => ({ ...f, roomName: e.target.value }))}
              placeholder="e.g. Boardroom A"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.roomDescription}
              onChange={e => setForm(f => ({ ...f, roomDescription: e.target.value }))}
              placeholder="Brief description of the room"
              rows={2}
              className="resize-none mt-1"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="device">Device Type</Label>
            <Select value={form.device} onValueChange={value => setForm(f => ({ ...f, device: value }))}>
              <SelectTrigger id="device" className="mt-1">
                <SelectValue placeholder="Select device type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="InMeet Room Pro">InMeet Room Pro</SelectItem>
                <SelectItem value="InMeet Room Plus">InMeet Room Plus</SelectItem>
                <SelectItem value="InMeet Room Mini">InMeet Room Mini</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Set room password"
              type="text"
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!form.roomName.trim() || !form.password.trim()}
            onClick={() => onSave({ ...room, ...form })}
          >
            {isEdit ? "Save Changes" : "Add Room"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface BoardRoomViewProps {
  room: Room
  onBack: () => void
}
function BoardRoomView({ room, onBack }: BoardRoomViewProps) {
  const [copiedKey, setCopiedKey]  = useState(false)
  const [copiedPwd, setCopiedPwd]  = useState(false)

  const copy = (text: string, type: "key" | "pwd") => {
    navigator.clipboard.writeText(text)
    if (type === "key") { setCopiedKey(true); setTimeout(() => setCopiedKey(false), 2000) }
    else                { setCopiedPwd(true); setTimeout(() => setCopiedPwd(false), 2000) }
  }


  return (
    <div className="flex flex-col h-full p-4 gap-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="h-8 w-8 rounded-full shrink-0" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-foreground">{room.roomName}</h2>
          <p className="text-sm text-muted-foreground">{room.roomDescription}</p>
        </div>
        <Badge className={`text-xs capitalize ${STATUS_STYLES[room.status]}`}>
          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 inline-block ${STATUS_DOT[room.status]}`} />
          {room.status}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
        {/* Room Credentials */}
        <div className="lg:col-span-2 bg-card rounded-2xl border border-stroke-100 dark:border-border p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" />
            <h3 className="text-base font-semibold text-foreground">Room Credentials</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-primary">Activation Key</p>
                  <button
                    onClick={() => copy(room.activationKey, "key")}
                    className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-primary/20 transition-colors"
                  >
                    {copiedKey ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-primary" />}
                  </button>
                </div>
                <p className="text-lg font-mono font-bold text-foreground">{room.activationKey}</p>
              </div>

              <div className="p-4 bg-gradient-to-br from-amber/5 to-orange/10 rounded-xl border border-amber/20">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-amber-600 dark:text-amber-400">Password</p>
                  <button
                    onClick={() => copy(room.password, "pwd")}
                    className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-amber/20 transition-colors"
                  >
                    {copiedPwd ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
                  </button>
                </div>
                <p className="text-lg font-mono font-bold text-foreground">{room.password}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="p-4 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900/20 dark:to-gray-900/20 rounded-xl border border-slate-200 dark:border-border dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Monitor className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Device</p>
                </div>
                <p className="text-sm font-semibold text-foreground">{room.device}</p>
              </div>

              <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border border-emerald-200 dark:border-emerald-700">
                <div className="flex items-center gap-2 mb-2">
                  <Tv2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Last Active</p>
                </div>
                <p className="text-sm font-semibold text-foreground">{room.lastActive}</p>
              </div>
            </div>
          </div>
        </div>

        {/* QR Code */}
        <div className="bg-card rounded-2xl border border-stroke-100 dark:border-border p-6 flex flex-col items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            <h3 className="text-base font-semibold text-foreground">Scan to Activate</h3>
          </div>
          <div className="bg-card p-4 rounded-xl shadow-lg border border-gray-200">
            <img 
              src={assetQRDeWP} 
              alt="QR Code" 
              width={180} 
              height={180} 
              className="rounded"
            />
          </div>
          <div className="text-center space-y-2">
            <p className="text-sm font-medium text-foreground">
              InMeet Room Activation
            </p>
            <p className="text-xs text-muted-foreground max-w-48">
              Scan this QR code with the InMeet Room app to automatically configure your device
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ManageRooms() {
  const [rooms, setRooms]           = useState<Room[]>(MOCK_ROOMS)
  const [search, setSearch]         = useState("")
  const [showAdd, setShowAdd]       = useState(false)
  const [editRoom, setEditRoom]     = useState<Room | null>(null)
  const [deleteRoom, setDeleteRoom] = useState<Room | null>(null)
  const [boardRoom, setBoardRoom]   = useState<Room | null>(null)

  const filtered = useMemo(() =>
    rooms.filter(r => !search || r.roomName.toLowerCase().includes(search.toLowerCase())),
    [rooms, search]
  )

  const handleSave = (data: Partial<Room>) => {
    if (data.id) {
      setRooms(prev => prev.map(r => r.id === data.id ? { ...r, ...data } as Room : r))
    } else {
      const newRoom: Room = {
        id: `r-${Date.now()}`,
        roomName: data.roomName!,
        roomDescription: data.roomDescription ?? "",
        activationKey: data.activationKey!,
        password: data.password!,
        device: data.device ?? "InMeet Room Pro",
        status: "offline",
        lastActive: "Never",
      }
      setRooms(prev => [newRoom, ...prev])
    }
    setShowAdd(false)
    setEditRoom(null)
  }

  const confirmDelete = () => {
    if (deleteRoom) {
      setRooms(prev => prev.filter(r => r.id !== deleteRoom.id))
      setDeleteRoom(null)
    }
  }

  if (boardRoom) return <BoardRoomView room={boardRoom} onBack={() => setBoardRoom(null)} />

  return (
    <div className="flex flex-col h-full gap-4 p-4 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold text-foreground">Manage Rooms</h2>
          <p className="text-xs text-muted-foreground">{rooms.length} room{rooms.length !== 1 ? "s" : ""} configured</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search rooms…"
              className="bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground w-36"
            />
          </div>
          <Button size="sm" className="gap-1.5 h-9" onClick={() => setShowAdd(true)}>
            <Plus className="w-4 h-4" /> Add Room
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 shrink-0">
        {[
          { 
            label: "Online", 
            count: rooms.filter(r => r.status === "online").length, 
            icon: <Monitor className="w-4 h-4 text-emerald-500" />, 
            bg: "bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20" 
          },
          { 
            label: "Idle", 
            count: rooms.filter(r => r.status === "idle").length, 
            icon: <Tv2 className="w-4 h-4 text-amber-500" />, 
            bg: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20" 
          },
          { 
            label: "Offline", 
            count: rooms.filter(r => r.status === "offline").length, 
            icon: <Lock className="w-4 h-4 text-slate-500 dark:text-slate-400" />, 
            bg: "bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900/20 dark:to-gray-900/20" 
          },
        ].map(({ label, count, icon, bg }) => (
          <div key={label} className="relative bg-card dark:bg-card border border-border rounded-2xl p-4 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden">
            {/* Decorative Background Wave */}
            <div className={`absolute inset-0 ${bg}`}>
              <svg 
                className="absolute bottom-0 right-0 w-full h-full" 
                viewBox="0 0 200 120" 
                fill="none" 
                preserveAspectRatio="none"
              >
                <path 
                  d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" 
                  fill="currentColor" 
                  className="text-white/30 dark:text-white/10"
                />
              </svg>
            </div>

            {/* Content */}
            <div className="relative z-10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-card/50 dark:bg-card/10 flex items-center justify-center shrink-0">
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-2xl font-bold text-foreground leading-none">{count}</div>
                <div className="text-sm font-medium text-muted-foreground mt-1">{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Rooms Table */}
      <div className="bg-card rounded-2xl border border-border flex-1 overflow-hidden flex flex-col">
        {filtered.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Tv2 className="w-12 h-12 text-muted-foreground/30" />
            <p className="text-sm font-medium text-muted-foreground">No rooms found</p>
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-4 h-4 mr-1.5" /> Add your first room
            </Button>
          </div>
        ) : (
          <div className="overflow-auto flex-1">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Room Name</TableHead>
                  <TableHead className="text-left px-4 py-3 text-xs font-medium text-muted-foreground hidden md:table-cell">Activation Key</TableHead>
                  <TableHead className="text-left px-4 py-3 text-xs font-medium text-muted-foreground hidden sm:table-cell">Password</TableHead>
                  <TableHead className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Status</TableHead>
                  <TableHead className="text-left px-4 py-3 text-xs font-medium text-muted-foreground hidden lg:table-cell">Device</TableHead>
                  <TableHead className="text-left px-4 py-3 text-xs font-medium text-muted-foreground hidden lg:table-cell">Last Active</TableHead>
                  <TableHead className="w-24 px-4 py-3 text-xs font-medium text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(room => (
                  <TableRow key={room.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <TableCell className="px-4 py-3">
                      <button
                        onClick={() => setBoardRoom(room)}
                        className="text-sm font-medium text-primary hover:underline text-left"
                      >
                        {room.roomName}
                      </button>
                      <p className="text-[10px] text-muted-foreground truncate max-w-[160px]">{room.roomDescription}</p>
                    </TableCell>
                    <TableCell className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs font-mono text-foreground/70">{room.activationKey}</span>
                    </TableCell>
                    <TableCell className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-xs font-mono text-foreground/70">{room.password}</span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <Badge className={`text-[10px] capitalize ${STATUS_STYLES[room.status]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 inline-block ${STATUS_DOT[room.status]}`} />
                        {room.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">{room.device}</span>
                    </TableCell>
                    <TableCell className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">{room.lastActive}</span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          title="View / QR Code"
                          onClick={() => setBoardRoom(room)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
                        >
                          <QrCode className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        <button
                          title="Edit"
                          onClick={() => setEditRoom(room)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => setDeleteRoom(room)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add / Edit Dialog */}
      {(showAdd || editRoom) && (
        <AddEditDialog
          room={editRoom}
          onSave={handleSave}
          onClose={() => { setShowAdd(false); setEditRoom(null) }}
        />
      )}

      {/* Delete Confirm */}
      <Dialog open={!!deleteRoom} onOpenChange={() => setDeleteRoom(null)}>
        <DialogContent className="max-w-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <DialogHeader>
              <DialogTitle>Delete Room</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground text-center">
              Are you sure you want to delete <strong>"{deleteRoom?.roomName}"</strong>? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRoom(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
